<script setup lang="ts">
import { AwSkeleton } from "@asap/design";
import { statusColorName } from "./theme";
import type { LeverItem } from "./types";

const SKELETON_COUNT = 3;
const VISUAL_SIZE = 44;
const RING_STROKE_WIDTH = 4;

withDefaults(
  defineProps<{
    title?: string;
    subtitle?: string;
    levers?: LeverItem[];
    loading?: boolean;
  }>(),
  { title: "Leviers", subtitle: "Chiffres sous l'objectif", levers: undefined, loading: false }
);

const ui = {
  list: "flex flex-col divide-y divide-secondary-200 rounded-xl border border-secondary-200 bg-white px-4",
  item: "flex items-center gap-3 py-3",
  ringBadge: "text-xs font-semibold",
  squareBadge: "flex size-11 shrink-0 items-center justify-center rounded-lg text-sm font-semibold",
  text: "text-sm leading-snug",
  title: "text-secondary-900",
  description: "text-secondary-700",
  skeletonVisual: "size-11 shrink-0 rounded-full",
  skeletonText: "flex w-full flex-col gap-1.5",
  skeletonTitle: "h-3.5 w-28",
  skeletonDescription: "h-3.5 w-full",
};
</script>

<template>
  <ConsultantKpiCard :title="title" :subtitle="subtitle" stacked-subtitle flat>
    <div :class="ui.list">
      <template v-if="loading">
        <div v-for="index in SKELETON_COUNT" :key="`lever-skeleton-${index}`" :class="ui.item">
          <AwSkeleton :class="ui.skeletonVisual" />
          <div :class="ui.skeletonText">
            <AwSkeleton :class="ui.skeletonTitle" />
            <AwSkeleton :class="ui.skeletonDescription" />
          </div>
        </div>
      </template>
      <template v-else>
        <div v-for="lever in levers" :key="lever.title" :class="ui.item">
          <ConsultantKpiProgressRing
            v-if="lever.progressPercent !== undefined"
            :percent="lever.progressPercent"
            :status="lever.status"
            :size="VISUAL_SIZE"
            :stroke-width="RING_STROKE_WIDTH"
          >
            <span :class="[ui.ringBadge, `text-${statusColorName(lever.status)}-600`]">{{ lever.badge }}</span>
          </ConsultantKpiProgressRing>
          <span
            v-else
            :class="[
              ui.squareBadge,
              `bg-${statusColorName(lever.status)}-50`,
              `text-${statusColorName(lever.status)}-600`,
            ]"
          >
            {{ lever.badge }}
          </span>
          <p :class="ui.text">
            <span :class="ui.title">{{ lever.title }}</span>
            <span :class="ui.description"> — {{ lever.description }}</span>
          </p>
        </div>
      </template>
    </div>
  </ConsultantKpiCard>
</template>
